/**
 * The countdown's bands and its ring geometry (components.md §2.1).
 *
 * **The old bands do not transfer, and that is the whole point of this file.**
 * The previous design used `>60s / 60–15s / <15s`. On a 60-second market the
 * first band never occurs and the second covers the entire tradable window, so
 * every market would render permanently amber — which is the same as rendering
 * nothing. The bands below are re-derived from the seconds that actually exist —
 * **sixty of them since register §F24**, where the first draft of this file said
 * ~47 — and each cut means something operationally:
 *
 * - **30s** is where the observed volume profile says the market stops being a
 *   quiet quote and starts being a race. It is also where D12's `p(1−p)` fee
 *   curve peaks, for when the rate stops being zero.
 * - **10s** is the length of the settlement window *and* the point at which D9
 *   requires 10 Hz rendering, so the visual change and the technical change
 *   happen at the same instant rather than at two arbitrary ones.
 * - **3s** is roughly the shortest interval in which a human can still read a
 *   book, decide, and click.
 *
 * The boundaries are measured against `expiry_at_ms`, so a shorter window
 * shortens only the first band and leaves the two that matter untouched.
 *
 * **The server is the sole authority on state.** Nothing here decides what state
 * a market is in — it is passed one, and it renders it. If the client's clock
 * reads `0.4` and a `market.{id}` frame says `EXPIRED`, the frame wins
 * immediately and visibly (D9, §2.1).
 *
 * No React, no DOM.
 */

import type { MarketState } from '@/api/http/types'
import { COUNTDOWN_DECIMAL_THRESHOLD_MS } from '@/lib/format'

export type CountdownBand = 'FIXING' | 'OPEN' | 'LATE' | 'FINAL' | 'LAST_CALL' | 'EXPIRED'

/** 30s. Where the book thickens and most volume arrives. */
export const LATE_BAND_MS = 30_000
/** 10s. Also `COUNTDOWN_DECIMAL_THRESHOLD_MS`, and also the settlement window —
 *  one number, three meanings, deliberately. */
export const FINAL_BAND_MS = COUNTDOWN_DECIMAL_THRESHOLD_MS
/** 3s. */
export const LAST_CALL_BAND_MS = 3_000

/** 4 Hz normally; 10 Hz once the decimal appears (D9). */
export const TICK_INTERVAL_MS = 250
export const FINAL_TICK_INTERVAL_MS = 100

export interface CountdownInput {
  /** The server's state, which outranks every calculation here. */
  state: MarketState
  anchorAtMs: number
  /** Counted down to during `FIXING`. Equal to `anchorAtMs` on the happy path
   *  since §F24, so the `FIXING` band is normally unreachable for the market on
   *  screen — it belongs to a successor the user is not looking at yet. */
  openAtMs: number
  expiryAtMs: number
  /** The corrected server-time estimate. Never `Date.now()` (D9). */
  nowMs: number
}

export interface CountdownView {
  band: CountdownBand
  /** Milliseconds to the deadline this band counts to — `open_at_ms` during
   *  `FIXING`, `expiry_at_ms` otherwise. Never negative. */
  remainingMs: number
  /** `true` once the decimal appears, which is also when 10 Hz starts. */
  decimal: boolean
  tickIntervalMs: number
  /** The ring's swept fraction over the **full 60 seconds**. Since register §F24
   *  the market's life and its tradable window are the same interval, so this is
   *  also the tradable fraction; it was not, when trading began 13s after the
   *  anchor. */
  ringFraction: number
  /** Where the tradable arc starts, as a fraction — the pre-open window drawn
   *  in `border-subtle` so it is visible rather than implied.
   *
   *  **Normally `0` since register §F24**, because `open_at_ms == anchor_ms`:
   *  the strike window closed before the market anchored. It was `13/60`. A
   *  non-zero value now means a late strike, which is precisely the case worth
   *  drawing, and it is drawn without a code change because this was always
   *  computed from the payload rather than from a constant. */
  preOpenFraction: number
}

export function countdownView(input: CountdownInput): CountdownView {
  const { state, anchorAtMs, openAtMs, expiryAtMs, nowMs } = input
  const periodMs = Math.max(1, expiryAtMs - anchorAtMs)
  const preOpenFraction = clamp01((openAtMs - anchorAtMs) / periodMs)
  const elapsed = clamp01((nowMs - anchorAtMs) / periodMs)

  // The state machine first, always. A market the server calls `EXPIRED` is
  // expired whatever the arithmetic says, and one it has not yet opened counts
  // to a different deadline.
  if (isPastExpiry(state)) {
    return {
      band: 'EXPIRED',
      remainingMs: 0,
      decimal: false,
      tickIntervalMs: TICK_INTERVAL_MS,
      ringFraction: 1,
      preOpenFraction,
    }
  }

  if (state === 'SCHEDULED' || state === 'FIXING') {
    return {
      band: 'FIXING',
      remainingMs: Math.max(0, openAtMs - nowMs),
      decimal: false,
      tickIntervalMs: TICK_INTERVAL_MS,
      ringFraction: elapsed,
      preOpenFraction,
    }
  }

  const remainingMs = Math.max(0, expiryAtMs - nowMs)
  const band = bandFor(remainingMs)
  const decimal = remainingMs < FINAL_BAND_MS
  return {
    band,
    remainingMs,
    decimal,
    tickIntervalMs: decimal ? FINAL_TICK_INTERVAL_MS : TICK_INTERVAL_MS,
    ringFraction: elapsed,
    preOpenFraction,
  }
}

function bandFor(remainingMs: number): CountdownBand {
  if (remainingMs <= 0) return 'EXPIRED'
  if (remainingMs < LAST_CALL_BAND_MS) return 'LAST_CALL'
  if (remainingMs < FINAL_BAND_MS) return 'FINAL'
  if (remainingMs < LATE_BAND_MS) return 'LATE'
  return 'OPEN'
}

/**
 * States in which the countdown hands off.
 *
 * `<ResolutionPanel>` takes the slot from here (§2.8) — Phase 7. Until then the
 * band is rendered honestly and nothing else happens, which is the line the
 * Phase 4 brief draws in §1.
 */
function isPastExpiry(state: MarketState): boolean {
  switch (state) {
    case 'EXPIRED':
    case 'RESOLVING':
    case 'RESOLVING_DEGRADED':
    case 'RESOLVED':
    case 'SETTLED':
    case 'VOIDED':
    case 'REFUNDED':
    case 'CANCELLED':
      return true
    case 'SCHEDULED':
    case 'FIXING':
    case 'OPEN':
      return false
  }
  return false
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(1, value))
}

// ---------------------------------------------------------------------------
// The determinate bar
// ---------------------------------------------------------------------------

/**
 * One determinate bar, for two windows that are no longer the same one
 * (components.md §2.5).
 *
 * Determinate, not an indeterminate shimmer: both windows have a known,
 * server-published duration, and a spinner over a bounded ten or thirteen
 * seconds reads as "something is wrong" when nothing is. **Driven by the
 * server's deadline** — `open_at_ms` or `resolve_eta_ms` — never by a hardcoded
 * 13,000.
 *
 * Until register §F24 market N's `RESOLVING` window *was* market N+1's `FIXING`
 * window, one function served both and the two bars agreed to the millisecond.
 * They are disjoint now: `FIXING(N+1)` runs `[anchor(N+1) − W, anchor(N+1))` and
 * ends where `RESOLVING(N)` begins. One function still serves both because the
 * arithmetic is the same — a start, a deadline and a clock — and because the
 * *caller* now supplies the start rather than assuming it (§F24d defect 3).
 */
export function determinateProgress(
  startMs: number | null,
  deadlineMs: number | null,
  nowMs: number,
): number | undefined {
  if (startMs === null || deadlineMs === null) return undefined
  const span = deadlineMs - startMs
  if (!Number.isFinite(span) || span <= 0) return undefined
  return clamp01((nowMs - startMs) / span)
}
